import React, { useState, useEffect } from 'react';
import { Card, CardText, CardTitle, Col, Input, Label, ListGroup, ListGroupItem, Row, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import LoaderButton from '../../loader-button/LoaderButton';
import BZAPIService from '../../../services/BZAPIService';
import PropTypes from 'prop-types';
import { includes } from 'lodash';

// Components may arrive from the API as plain strings or as {label, value}
// objects (e.g. ServiceNow). The available-list renders and keys items as
// strings (id={value}, <ListGroupItem>{value}</ListGroupItem>), so normalize to
// the display name — rendering a raw object as a React child crashes the whole
// Configuration tab ("Objects are not valid as a React child").
const toComponentName = c => (c && typeof c === 'object' ? c.label ?? c.value ?? '' : c);

export default function ComponentFilterPrototype({
  vendorId,
  vendorLabel,
  loader,
  setLoader,
  setQuery,
  query,
  offset,
  limit,
  includedComponents,
  setIncludedComponents,
  excludedComponents,
  setExcludedComponents
}) {
  const [activeItems, setActiveItems] = useState([]);
  const [search, setSearch] = useState('');
  const [availableComponents, setAvailableComponents] = useState();

  useEffect(() => {
    setLoader(true);
    if (includedComponents && includedComponents.length) {
      let excluded = excludedComponents.map(ex => ex.value);
      const names = includedComponents.map(toComponentName);
      setAvailableComponents(names.filter(name => !excluded.includes(name)));
    } else {
      setAvailableComponents([]);
    }
    setLoader(false);
  }, [includedComponents.length, excludedComponents, setLoader, includedComponents]);

  function excludeComponent() {
    setLoader(true);
    const availableCopy = availableComponents;
    const c = availableCopy.filter(item => includes(activeItems, item));
    const d = c.map(item => {
      return { label: item, value: item };
    });
    setExcludedComponents(excludedComponents.concat(d));
    setAvailableComponents(availableCopy.filter(item => !includes(activeItems, item)));
    // setSearch('');
    setActiveItems([]);
    setLoader(false);
  }
  async function includeComponent() {
    setLoader(true);

    const excludedFiltered = excludedComponents.filter(el => {
      return !activeItems.some(f => {
        return f === el.value && f === el.label;
      });
    });

    setExcludedComponents(excludedFiltered);

    let components = (await BZAPIService.getComponents(vendorId, query, offset, limit)) || [];
    setIncludedComponents(components && components.results.length ? components.results : ['No results found']);
    let excluded = excludedComponents.map(ex => ex.value);
    setAvailableComponents(includedComponents.filter(item => includes(excluded, item)));
    setActiveItems([]);

    setLoader(false);
  }
  //   const toggleActive = e => {
  //     const item = e.target.id;
  //     if (item !== 'No results found') setActiveListItem({ id: item });
  //   };

  const selectComponent = e => {
    const item = e.target.id;
    // console.log('item: ', item);
    if (!includes(activeItems, item)) {
      setActiveItems([...activeItems, item]);
    } else {
      setActiveItems(activeItems.filter(i => i !== item));
    }
    // console.log('active items: ', activeItems);
  };

  const handleSearchChange = async e => {
    const k = e.target.value;
    setSearch(k);
    setQuery(k);

    if (k.length >= 2) {
      setLoader(true);

      let components = (await BZAPIService.getComponents(vendorId, k, offset, limit)) || [];
      setIncludedComponents(components && components.results.length ? components.results : ['No results found']);
      setLoader(false);
    }
  };

  const handleBlankSearch = async e => {
    setLoader(true);

    let components = (await BZAPIService.getComponents(vendorId, '', offset, limit)) || [];
    setIncludedComponents(components && components.results.length ? components.results : ['No results found']);
    setLoader(false);
  };

  return (
    <>
      <div className="mb-4">
        <CardTitle data-testid={'component-filter-label'}>Filter {vendorLabel} Components</CardTitle>
        <CardText>Select multiple entries if necessary</CardText>
        <Row>
          <Col md={5} className="mb-2">
            <Input
              type="text"
              name="search"
              id="search"
              placeholder="Search..."
              value={search}
              onChange={e => handleSearchChange(e)}
              onKeyDown={e => e.target.value.length === 0 && e.key === 'Enter' && handleBlankSearch(e)}
              data-testid={'component-filter-search'}
            />
          </Col>
        </Row>
        <Row>
          <Col md="5">
            <Row className="mt-2">
              <Col>
                <Label>Available Components</Label>
              </Col>
              <Col className="text-right mb-2">
                <Button
                  size="sm"
                  className="btn btn-link text-muted p-0"
                  onClick={e => setActiveItems(!activeItems.length ? availableComponents : [])}
                  data-testid={'availableComponents-select-all-btn'}
                  disabled={!availableComponents?.length}
                >
                  {activeItems?.length && includes(availableComponents, activeItems[0]) ? 'Deselect all' : 'Select all'}
                </Button>
              </Col>
            </Row>

            <Card className="bg-white">
              {/* <p className="text-danger">{componentError}</p> */}
              <ListGroup
                style={{ maxHeight: '400px', minHeight: '400px', overflowY: 'scroll' }}
                data-testid={'available-components-list'}
              >
                {availableComponents?.length ? (
                  availableComponents.map((value, index) => (
                    <ListGroupItem
                      key={index + value + '-proto'}
                      id={value}
                      role="button"
                      style={{ border: '0px' }}
                      onClick={e => selectComponent(e)}
                      active={includes(activeItems, value)}
                    >
                      {value}
                    </ListGroupItem>
                  ))
                ) : (
                  <ListGroupItem style={{ border: '0px' }}>No results found</ListGroupItem>
                )}
              </ListGroup>
            </Card>
          </Col>
          <Col md={2} className="align-middle text-center my-auto">
            <Row>
              <Col className="align-middle text-center my-auto">
                <LoaderButton
                  onClick={excludeComponent}
                  isLoading={loader}
                  color="falcon-default"
                  size="sm"
                  className="mt-2"
                  data-testid={'exclude-btn'}
                  disabled={!availableComponents?.length}
                  //   disabled={
                  //     !activeListItem?.id?.length || !availableComponents.includes(activeListItem.id) ? true : false
                  //   }
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </LoaderButton>
              </Col>
            </Row>
            <Row>
              <Col className="align-middle text-center my-auto">
                <LoaderButton
                  onClick={includeComponent}
                  isLoading={loader}
                  color="falcon-default"
                  size="sm"
                  className="mt-2"
                  data-testid={'include-btn'}
                  disabled={!excludedComponents?.length}
                  //   disabled={
                  // !activeListItem?.id?.length ||
                  // (!excludedComponents.filter(co => co.value.includes(activeListItem.id)) &&
                  // availableComponents.includes(activeListItem.id)
                  //   ? true
                  //   : false
                  //   }
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </LoaderButton>
              </Col>
            </Row>
          </Col>
          <Col md="5">
            <Row className="mt-2">
              <Col>
                <Label>{vendorLabel} Components to Exclude</Label>
              </Col>
              <Col className="text-right mb-2">
                <Button
                  size="sm"
                  className="btn btn-link text-muted p-0"
                  onClick={e => setActiveItems(!activeItems.length ? excludedComponents.map(item => item.label) : [])}
                  data-testid={'availableComponents-select-all-btn'}
                  disabled={!excludedComponents?.length}
                >
                  {activeItems?.length &&
                  excludedComponents.filter(ex => {
                    return includes(ex, activeItems[0]);
                  })?.length
                    ? 'Deselect all'
                    : 'Select all'}
                </Button>
              </Col>
            </Row>
            <Card className="bg-white">
              <ListGroup
                style={{ maxHeight: '400px', minHeight: '400px', overflowY: 'scroll' }}
                data-testid={'excluded-components-list'}
              >
                {excludedComponents.map((value, index) => (
                  <ListGroupItem
                    key={index + value + '-proto'}
                    role="button"
                    style={{ border: '0px' }}
                    id={value.label}
                    onClick={e => selectComponent(e)}
                    active={includes(activeItems, value.label)}
                  >
                    {value.label}
                  </ListGroupItem>
                ))}
              </ListGroup>
            </Card>
          </Col>
        </Row>
        <br />
      </div>
    </>
  );
}

ComponentFilterPrototype.propTypes = {
  vendorId: PropTypes.string,
  vendorLabel: PropTypes.string,
  includedComponents: PropTypes.array,
  setIncludedComponents: PropTypes.func,
  excludedComponents: PropTypes.array,
  setExcludedComponents: PropTypes.func,
  loader: PropTypes.bool,
  setLoader: PropTypes.func,
  setQuery: PropTypes.func,
  query: PropTypes.string,
  offset: PropTypes.string,
  limit: PropTypes.string
};
